<template>
  <NavigationLayout
    :navigationBarStyle="{
      'top': 'var(--safe-area-inset-top, 0px)',
    }"
    :navigationContentStyle="{
      'padding-top': 'calc(44px + var(--safe-area-inset-top, 0px))',
    }"
  >
    <div class="ly_cont" slot="contents">
      <section class="title">
        <h2>{{ $t('game_commentary__title') }}</h2>
      </section>
      <section class="requirements-card">
        <p :class="{ 'enabled-color': isEnabled, 'disabled-color': !isEnabled }"
          style="text-align: center; margin-bottom: 16px;">
          <CheckIcon :check="isEnabled" />
          {{ isEnabled ? $t('game_commentary__condition_cleared') : $t('game_commentary__condition_ng') }}
        </p>
        <p style="text-align: center;" v-html="$t('game_commentary__condition_explanation')"></p>
        <ul class="requirements">
          <li>
            <CheckIcon :check="enabledRegion" /> {{ $t('game_commentary__condition__body__region') }}
          </li>
          <li>
            <CheckIcon :check="enabledRank" /> {{ $t('game_commentary__condition__body__rank') }}
          </li>
        </ul>
        <div v-if="isEnabled || (enabledRegion && enabledRank)">
          <hr />
          <p>
            <CheckIcon :check="agoraRemainingSeconds > 0" /> {{ $t('game_commentary__condition__body__delivery_time') }}
          </p>
          <p id="reset-note" v-if="!enabledUnlimited">
            {{ $t('game_commentary__condition__caption__delivery_time') }}
          </p>
          <p id="enabledUnlimited" v-if="enabledUnlimited">
            {{ $t('game_commentary__condition__unlimited') }}
          </p>
          <ProgressBar :progress="remainingRate" />
          <p id="remaining-time">
            {{ remainingTimeText }}
          </p>
        </div>
      </section>
      <a :href="guidelineUrl">
        <section class="guide-card blue-bg">
          <div class="guide-info">
            <p>{{ $t('game_commentary__button__delivery_guidelines') }}</p>
          </div>
          <img src="/img/obs/info_img1.png" alt="delivery_guidelines">
        </section>
      </a>
      <a :href="howToUrl">
        <section class="guide-card purple-bg">
          <div class="guide-info">
            <p>{{ $t('game_commentary__button__guide') }}</p>
          </div>
          <img src="/img/obs/info_img2.png" alt="guide">
        </section>
      </a>
      <section class="download_obs_plugin">
        <h3>
          <img class="obs_icon" src="/img/obs/icon_obs.png">
          {{ $t('game_commentary__sub_title__obs_plugin_download') }}
        </h3>
        <p>{{ $t('game_commentary__body__obs_plugin_download') }}</p>
        <a :href="obsPluginUrlWin">
          <ButtonSubmit class="button" title="Windows" />
        </a>
        <a :href="obsPluginUrlOSX">
          <ButtonSubmit class="button" title="Mac" />
        </a>
      </section>
      <section>
        <h3>
          <img class="obs_icon" src="/img/obs/icon_obs.png">
          {{ $t('game_commentary__sub_title__obs_one_time_token') }}
        </h3>
        <p>{{ $t('game_commentary__body__obs_one_time_token') }}</p>
        <input id="obs_token" slot="actionInput" :value="obsToken" disabled type="text" name="count" size="30"
          required />
        <button id="copy_button" @click="copyClipboard()">
          <img src="/img/obs/icon_copy.png" />
          <div v-if="popupVisible" id="copy_popup">{{ $t('common__copied') }}</div>
        </button>
      </section>
    </div>
  </NavigationLayout>
</template>

<script lang="ts">
import Vue from 'vue'
import NavigationLayout from '@/components/NavigationLayout.vue'
import PrimaryButton from '@/components/PrimaryButton.vue'
import CoinPurchasePending from '@/components/Mypage/CoinPurchasePending.vue'
import ButtonSubmit from '@/components/ButtonSubmit.vue'
import { isProduction } from '@/utils/env'
import CheckIcon from '@/components/CheckIcon.vue'
import ProgressBar from '@/components/ProgressBar.vue'
import { api } from '@/api/appGatewayServerApi'
import Component from 'vue-class-component'
import { checkInMaintenance } from '@/utils/maintenance'
import { handleUnauthorized } from '@/utils/handle_error'
import i18n from '@/i18n'

@Component({
  components: {
    NavigationLayout,
    CoinPurchasePending,
    PrimaryButton,
    ButtonSubmit,
    CheckIcon,
    ProgressBar,
  },
})
export default class ObsStreamPage extends Vue {
  // ガイドラインと手順ガイドは本番向けのお知らせページしかないので共通のURLを設定
  guidelineUrl: string = 'https://reality.app/notice/page/jtihzvswrqzr'
  howToUrl: string = 'https://reality.app/notice/page/w2ds0pt4janz'
  popupVisible: boolean = false
  obsToken: string = ''
  agoraAvailableHoursPerMonth: number = 0
  agoraRemainingSeconds: number = 0
  enabledRegion: boolean = false
  enabledRank: boolean = false
  pluginLatestVersion: string = '1.1.0'
  pluginUrlDomain: string = isProduction()
    ? 'https://reality-public.akamaized.net/obs/'
    : 'https://storage.googleapis.com/vlive-lab-public-origin/obs/'
  async created(): Promise<void> {
    try {
      await this.fetchUserInfo();
      await this.generateToken();
    } catch (e) {
      if (checkInMaintenance(e)) {
        alert(this.$t('order_details__alert__cancel_operation'))
        this.$router.push({ path: '/maintenance' }).catch(() => {})
        return
      }
      handleUnauthorized(e)
    }
  }
  get isEnabled(): boolean {
    return this.agoraRemainingSeconds > 0
  }

  // 利用時間が無制限かどうか
  get enabledUnlimited(): boolean {
    // 月1000時間以上だったら実質無制限だし無制限として判定する
    return this.agoraAvailableHoursPerMonth >= 1000
  }

  // 残りのゲーム実況配信可能時間 (時間)
  get remainingHours(): number {
    return Math.floor(this.agoraRemainingSeconds / (60 * 60))
  }

  // 残りのゲーム実況配信可能時間（分）
  get remainingMinutes(): number {
    // 残り時間が1分未満の場合は1分として表示
    if (0 < this.agoraRemainingSeconds && this.agoraRemainingSeconds < 60) {
      return 1
    }
    return Math.floor(this.agoraRemainingSeconds / 60) % 60
  }

  // 残りのゲーム実況配信可能時間の表示テキスト
  get remainingTimeText(): string {
    const restText = i18n.t('game_commentary__condition__caption__rest').toString()
    if (this.enabledUnlimited) {
      return restText + '∞'
    }
    if (this.remainingHours > 0) {
      return restText + i18n.t('game_commentary__condition__caption__hours_min', {
        hour: this.remainingHours,
        min: this.remainingMinutes
      }).toString()
    }
    return restText + i18n.t('game_commentary__condition__caption__min', {
      min: this.remainingMinutes
    }).toString()
  }

  get obsPluginUrlOSX(): string {
    const fileName = isProduction()
      ? `REALITY-OBS-Plugin-${this.pluginLatestVersion}-Installer.pkg`
      : `lab-REALITY-OBS-Plugin-${this.pluginLatestVersion}-Installer.pkg`
    return this.pluginUrlDomain + fileName
  }

  get obsPluginUrlWin(): string {
    const fileName = isProduction()
      ? `REALITY-OBS-Plugin-${this.pluginLatestVersion}-Installer.exe`
      : `lab-REALITY-OBS-Plugin-${this.pluginLatestVersion}-Installer.exe`
    return this.pluginUrlDomain + fileName
  }

  // 残りのゲーム実況配信可能時間の割合
  get remainingRate(): number {
    if (this.enabledUnlimited) {
      return 100
    }
    if (this.agoraAvailableHoursPerMonth === 0) {
      return 0
    }
    let rate = Math.round((this.agoraRemainingSeconds / (this.agoraAvailableHoursPerMonth * 60 * 60)) * 100)
    return Math.min(rate, 100)
  }

  async fetchUserInfo() {
    await api().obs.get.info()
      .then((response) => {
        this.agoraAvailableHoursPerMonth = response.agoraAvailableHoursPerMonth
        this.agoraRemainingSeconds = response.agoraRemainingSeconds
        this.enabledRegion = response.enabledRegion
        this.enabledRank = response.enabledRank

        // MEMO: サーバーから最新のプラグインバージョンを取得するAPIはあるが、
        // そちらを変更するとOBSプラグイン側で古いバージョンのユーザーが使えなくなってしまう(強制アプデのような状態)
        // 1.1.0へのバージョンアップはOBSのバージョンに合わせたものであり、既存の1.0.0を使い続けても問題はないため、
        // WebContents側は最新バージョンを公開し、サーバー側は1.0.0のままとした。
        // this.pluginLatestVersion = response.pluginLatestVersion
      })
  }
  async generateToken() {
    await api().obs.get.token()
      .then((response) => {
        this.obsToken = response.token
      })
  }

  copyClipboard() {
    navigator.clipboard.writeText(this.obsToken)
    this.showPopup()
  }

  showPopup() {
    this.popupVisible = true
    setTimeout(() => {
      this.popupVisible = false
    }, 2000)
  }
}

</script>

<style scoped>
.ly_cont {
  position: relative;
  height: auto;
  min-height: 100%;
  padding: 24px 16px calc(24px + var(--safe-area-inset-bottom, 0px)) 16px;

  @media (min-width: 800px) {
    width: 800px;
    margin: 0 auto;
  }
}

h2 {
  text-align: center;
  color: #0F0F0F;
  font-size: 18px;
  font-weight: 600;
}

h3 {
  color: #000000;
  font-size: 16px;
  font-weight: 600;
}


p {
  font-size: 14px;
  margin-bottom: 10px;
}

section {
  margin-bottom: 32px;
}

hr {
  background-color: #bababa;
}

.primary_button {
  margin-top: 24px;
}

#copy_button {
  width: 24px;
  height: 24px;
  border: none;
  cursor: pointer;
  outline: none;
  padding: 0;
  appearance: none;
  margin-left: 10px;
  background-color: transparent;
}

#copy_button img {
  width: 100%;
  height: 100%;
}

.button {
  width: 80px;
  height: 24px;
  margin-left: 10px;
  border-radius: 12px;
  font-size: 12px;
}

#copy_button {
  position: relative;
}

#copy_popup {
  position: absolute;
  top: -40px;
  left: 14px;
  width: 80px;
  transform: translateX(-50%);
  background-color: #333;
  color: white;
  padding: 5px 10px;
  border-radius: 5px;
  transition: opacity 0.3s ease;
}

.streaming-guide {
  background-color: #ffffff;
  border-radius: 8px;
  padding: 20px;
  max-width: 400px;
  margin: 0 auto;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
}

.obs_icon {
  width: 24px;
  height: 24px;
}

.requirements-card {
  align-items: center;
  background-color: #f0f0f0;
  border-radius: 8px;
  padding: 16px;
}

.requirements-card p {
  font-weight: 600;
}

.requirements-card ul {
  list-style: none;
  font-weight: 600;
  padding-left: 0px;
  font-size: 14px;
}

.requirements-card li {
  padding-top: 16px;
}

.disabled-color {
  color: #FF003D;
}

.enabled-color {
  color: #188DFF;
}

.guide-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 40px;
  border-radius: 8px;
}

.blue-bg {
  background: linear-gradient(90deg, #2773EA 0%, #63B4F2 100%);
}

.purple-bg {
  background: linear-gradient(90deg, #8045FF 0%, #C154DC 100%);
}

.guide-card img {
  width: 120px;
  height: 120px;
  border-start-end-radius: 8px;
  border-end-end-radius: 8px;
}

.guide-info {
  margin-left: 20px;
}

.guide-info p {
  font-size: 16px;
  font-weight: 600;
  color: #ffffff;
}

#reset-note {
  font-weight: normal;
  font-size: 10px;
  color: #737373;
  margin-top: -8px;
  margin-bottom: 16px;
}

#remaining-time {
  text-align: center;
  font-size: 10px;
  font-weight: 400;
}

#enabledUnlimited {
  width: 100%;
  background-color: #c6e3ff;
  text-align: center;
  padding: 16px;
  border-radius: 8px;
  margin-top: 16px;
  margin-bottom: 16px;
}
</style>
